/**
 * Synthetic patient registry (PROD-579 + PROD-585).
 *
 * Exports the 4 canonical Phase 1 patient fixtures plus helpers. Per the
 * 2026-05-06 fixer round (PROD-585, COMPONENT-CONTRACTS §6 demo-gap field +
 * master v3.3 amendment Demo gap field):
 *   - A fixture that fails Zod validation does NOT throw at module load.
 *   - Instead the registry emits a sentinel
 *       `{ id, missing: true, error }`
 *     entry so the PatientPicker can render the missing patient as a
 *     disabled row with the canonical
 *     `[FIXTURE MISSING - {patientId} fixture not loaded. The 4 canonical
 *      profiles required: khalid-al-mansoori, fatima-al-zaabi,
 *      omar-al-rashidi, saeed-al-mazrouei.]`
 *     tooltip. Other rows remain selectable (HC1 graceful degradation).
 *
 * This preserves the data-integrity gate (a developer who breaks a fixture
 * still sees a console warning + the disabled UI surface) while honoring
 * the master v3.3 amendment requirement: a missing fixture must surface a
 * visible warning, never a silent fallback. Mirrors the pattern locked
 * for tenant configs in `src/data/tenant-configs/index.ts`.
 *
 * Tests: `tests/fixture-schemas.test.ts` covers the happy path against the
 * raw fixture imports; `tests/patient-picker.test.tsx` covers the
 * missing-fixture sentinel render.
 *
 * Future PROD-568 wires tenant filtering through `getPatientsForTenant`.
 * Phase 2 ClinicalDesk and Studio panels resolve the active patient via
 * `getPatientById` from `usePatientStore.activePatientId`.
 *
 * Per HC1 + ADR-PROTO-002 mitigation 4: every value is synthetic.
 */
import {
  patientFixtureSchema,
  type PatientFixture,
  type TenantId,
  type EthnicSubgroup,
} from './types';
export type { EthnicSubgroup };
export { ethnicSubgroupSchema } from './types';
import { khalidAlMansoori } from './khalid-al-mansoori';
import { fatimaAlZaabi } from './fatima-al-zaabi';
import { omarAlRashidi } from './omar-al-rashidi';
import { saeedAlMazrouei } from './saeed-al-mazrouei';

/**
 * Canonical patient ids in load-bearing order: Khalid (TLP1), Fatima
 * (TLP2), Omar (TLP3), Saeed (TLP4) per ADR-PROTO-002 + master What to
 * Build §2 Dynamic Patient Selection. The PatientPicker renders this
 * sequence verbatim; missing fixtures are surfaced as disabled rows in
 * the same slot. Single source of truth for the "4 canonical profiles
 * required" copy in the demo-gap tooltip.
 */
export const CANONICAL_PATIENT_IDS = [
  'khalid-al-mansoori',
  'fatima-al-zaabi',
  'omar-al-rashidi',
  'saeed-al-mazrouei',
] as const;
export type CanonicalPatientId = (typeof CANONICAL_PATIENT_IDS)[number];

/**
 * Sentinel emitted when a patient fixture fails schema validation. The
 * picker renders this as a disabled row with the canonical fixture-
 * missing tooltip; downstream consumers (chart resolver, NBA card) treat
 * `missing` as a hard guard.
 */
export interface MissingPatientFixture {
  readonly id: CanonicalPatientId;
  readonly missing: true;
  readonly error: string;
}

export type PatientRegistryEntry = PatientFixture | MissingPatientFixture;

const RAW_REGISTRY: ReadonlyArray<{ id: CanonicalPatientId; raw: unknown }> = [
  { id: 'khalid-al-mansoori', raw: khalidAlMansoori },
  { id: 'fatima-al-zaabi', raw: fatimaAlZaabi },
  { id: 'omar-al-rashidi', raw: omarAlRashidi },
  { id: 'saeed-al-mazrouei', raw: saeedAlMazrouei },
];

function parseRegistryEntry(entry: {
  id: CanonicalPatientId;
  raw: unknown;
}): PatientRegistryEntry {
  const parsed = patientFixtureSchema.safeParse(entry.raw);
  if (parsed.success) {
    // Defensive: if the fixture parses but the id field disagrees with
    // the canonical slot, log a warning so the developer notices the
    // registry/file mismatch. The parsed payload is still returned.
    if (parsed.data.id !== entry.id) {
      console.warn(
        `[synthetic-patients] Fixture in slot "${entry.id}" parsed with id "${parsed.data.id}".`,
      );
    }
    return parsed.data;
  }
  // Surface the schema error in the dev console so the developer sees
  // why the patient fell back to the disabled sentinel.
  console.warn(
    `[synthetic-patients] Fixture for "${entry.id}" failed schema validation; rendering as missing.`,
    parsed.error.flatten(),
  );
  return {
    id: entry.id,
    missing: true,
    error: parsed.error.message,
  };
}

/**
 * Full patient registry in canonical order. Always contains every
 * canonical id; entries are either a valid `PatientFixture` or a
 * `MissingPatientFixture` sentinel. The PatientPicker iterates this
 * registry directly so missing fixtures still occupy their slot.
 */
export const PATIENT_REGISTRY: ReadonlyArray<PatientRegistryEntry> =
  RAW_REGISTRY.map(parseRegistryEntry);

/**
 * Valid patient fixtures only (filters out sentinels). Backward-
 * compatible with the prior export shape so callers that do not need the
 * sentinels (default-patient resolver, tenant filter, store guard) keep
 * working.
 */
export const SYNTHETIC_PATIENTS: ReadonlyArray<PatientFixture> =
  PATIENT_REGISTRY.filter(
    (entry): entry is PatientFixture => !('missing' in entry),
  );

/** Type guard - is this registry entry a missing-fixture sentinel? */
export function isMissingPatientFixture(
  entry: PatientRegistryEntry,
): entry is MissingPatientFixture {
  return 'missing' in entry && entry.missing === true;
}

/**
 * Build the canonical fixture-missing tooltip text per
 * COMPONENT-CONTRACTS §6 demo-gap field. Quoted verbatim - the wording
 * is load-bearing for the master v3.3 amendment requirement and is
 * exported so tests can assert against the canonical string without
 * reproducing it.
 */
export function patientFixtureMissingTooltip(id: string): string {
  return `[FIXTURE MISSING - ${id} fixture not loaded. The 4 canonical profiles required: ${CANONICAL_PATIENT_IDS.join(', ')}.]`;
}

/** Resolve a fixture by id. Returns `undefined` if no fixture matches. */
export function getPatientById(id: string): PatientFixture | undefined {
  return SYNTHETIC_PATIENTS.find((p) => p.id === id);
}

/**
 * Filter patients by tenant. Future PROD-568 calls this from the
 * TenantProfileSwitcher to scope the picker.
 *
 * Apex Cardiometabolic = Khalid (TLP1) + Omar (TLP3).
 * Longevity Health Institute = Fatima (TLP2) + Saeed (TLP4).
 */
export function getPatientsForTenant(
  tenantId: TenantId,
): ReadonlyArray<PatientFixture> {
  return SYNTHETIC_PATIENTS.filter((p) => p.tenant === tenantId);
}

/**
 * First valid patient in canonical order; default selection on first
 * paint. Skips any leading missing-fixture sentinels per HC1 graceful
 * degradation. Throws if every fixture is missing - that boot-time
 * condition is explicitly out of contract for the prototype (the
 * PatientPicker still renders all 4 disabled rows because it iterates
 * `PATIENT_REGISTRY`, not `SYNTHETIC_PATIENTS`).
 */
export function getDefaultPatient(): PatientFixture {
  const first = SYNTHETIC_PATIENTS[0];
  if (!first) {
    throw new Error(
      '[synthetic-patients] No valid fixture available; every canonical patient failed schema validation. The PatientPicker still renders the [FIXTURE MISSING] rows but no default selection can be resolved.',
    );
  }
  return first;
}

export type { PatientFixture, TenantId } from './types';
export { patientFixtureSchema } from './types';
